import type { IconType } from 'react-icons';
import {
  FiAlertTriangle,
  FiBarChart2,
  FiBell,
  FiBook,
  FiCpu,
  FiDollarSign,
  FiFileText,
  FiFlag,
  FiFolder,
  FiGlobe,
  FiGrid,
  FiKey,
  FiLayout,
  FiLifeBuoy,
  FiPackage,
  FiTag,
  FiTrash2,
  FiTrendingUp,
  FiUserX,
  FiUsers,
  FiVolume2,
} from 'react-icons/fi';

export type AdminNavigationItem = {
  to: string;
  label: string;
  icon: IconType;
  pageLabel?: string;
};

export type AdminNavigationSection = {
  title: string;
  items: AdminNavigationItem[];
};

export type AdminPageContext = {
  section: string;
  label: string;
};

export const ADMIN_NAVIGATION_SECTIONS: AdminNavigationSection[] = [
  { title: 'Overview', items: [{ to: '/dashboard', label: 'Dashboard', icon: FiBarChart2 }] },
  {
    title: 'Analytics',
    items: [
      { to: '/analytics/financial', label: 'Financial', icon: FiDollarSign },
      { to: '/analytics/behavior', label: 'User behavior', icon: FiUsers },
      { to: '/analytics/generation-quality', label: 'Generation performance', icon: FiBarChart2 },
    ],
  },
  {
    title: 'App Management',
    items: [
      { to: '/categories', label: 'Categories', icon: FiFolder },
      { to: '/users', label: 'Users', icon: FiUsers },
      { to: '/extensions', label: 'Lenses', icon: FiGrid },
      { to: '/articles', label: 'Articles', icon: FiBook },
      { to: '/home-layout', label: 'Home Layout', icon: FiLayout },
      { to: '/localization', label: 'Localization', icon: FiGlobe },
      { to: '/waitlist', label: 'Waitlist', icon: FiFileText },
      { to: '/reports', label: 'Reports', icon: FiFlag },
      { to: '/account-deletions', label: 'Account Deletions', icon: FiUserX },
    ],
  },
  {
    title: 'Engagement',
    items: [
      { to: '/notifications', label: 'Notifications', icon: FiBell },
      { to: '/announcements', label: 'Announcements', icon: FiVolume2 },
      { to: '/boost', label: 'Boost', icon: FiTrendingUp },
      { to: '/promocodes', label: 'Promocodes', icon: FiTag },
    ],
  },
  {
    title: 'Finance Operations',
    items: [
      { to: '/payouts', label: 'Payouts', icon: FiDollarSign },
      { to: '/revenuecat-recovery', label: 'RC Recovery', icon: FiLifeBuoy },
    ],
  },
  {
    title: 'System & Settings',
    items: [
      { to: '/versions', label: 'Versions', icon: FiPackage },
      { to: '/models', label: 'AI Models', icon: FiCpu },
      { to: '/generation-usage', label: 'Generation diagnostics', icon: FiBarChart2 },
      { to: '/generation-failed-logs', label: 'Gen Failures', icon: FiAlertTriangle },
      { to: '/api-tokens', label: 'API Tokens', icon: FiKey },
      { to: '/storage', label: 'Storage GC', icon: FiTrash2 },
    ],
  },
];

const ADDITIONAL_PAGE_CONTEXTS: Array<AdminPageContext & { to: string }> = [
  { to: '/analytics/credits', section: 'Analytics', label: 'User behavior' },
  { to: '/dashboard/legacy', section: 'Overview', label: 'Legacy dashboard' },
  { to: '/boosted-creators', section: 'Engagement', label: 'Boosted Creators' },
];

function matchesPath(pathname: string, route: string) {
  return pathname === route || pathname.startsWith(`${route}/`);
}

/** Returns the sidebar-derived page context for a protected admin path. */
export function resolveAdminPageContext(pathname: string): AdminPageContext | null {
  const normalizedPathname = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const contexts = [
    ...ADMIN_NAVIGATION_SECTIONS.flatMap(section => section.items.map(item => ({
      to: item.to,
      section: section.title,
      label: item.pageLabel ?? item.label,
    }))),
    ...ADDITIONAL_PAGE_CONTEXTS,
  ];
  const match = contexts
    .filter(context => matchesPath(normalizedPathname, context.to))
    .sort((left, right) => right.to.length - left.to.length)[0];

  return match ? { section: match.section, label: match.label } : null;
}
